import { useState } from "react";
import AgentResponseLayout from "../../../components/agent/AgentResponseLayout";
import SelectInput from "../../../components/forms/SelectInput";
import MultiSelectInput from "../../../components/forms/MultiSelectInput";
import CheckboxGroup from "../../../components/forms/CheckboxGroup";
import NotesField from "../../../components/forms/NotesField";
import { useAgentResponse } from "../../../js/hooks/useAgentResponse";
import {
  ARCHITECTURAL_FEATURES,
  BATHROOM_DETAILS,
  BONUS_ROOMS,
  CABINET_STYLES,
  CEILING_HEIGHTS,
  CEILING_TYPES,
  COUNTERTOP_TYPES,
  FLOORING_TYPES,
  KITCHEN_DETAILS,
  KITCHEN_LAYOUTS,
  WALL_TYPES,
} from "../../../js/utils/agentResponseOptions";

const KITCHEN_SIZE = [
  { value: "compact", label: "Compact" },
  { value: "standard", label: "Standard" },
  { value: "large", label: "Large" },
  { value: "gourmet", label: "Gourmet / chef" },
];

export default function AgentResponseInterior() {
  const { response, saveSection, goBack, goNext } = useAgentResponse("interior");
  const saved = response?.interior || {};
  const [form, setForm] = useState(null);
  const current = form || saved;

  function update(event) {
    const { name, value } = event.target;
    setForm((prev) => ({ ...(prev || saved), [name]: value }));
  }

  async function handleContinue() {
    await saveSection(current);
    goNext();
  }

  if (!response) {
    return (
      <AgentResponseLayout title="Interior" onBack={goBack} onContinue={() => {}}>
        <p>Loading response…</p>
      </AgentResponseLayout>
    );
  }

  return (
    <AgentResponseLayout
      response={response}
      sectionKey="interior"
      title="Interior"
      lead="Mirror the buyer interior checklist: ceilings, walls, flooring, architecture, kitchen, baths, and bonus rooms."
      onBack={goBack}
      onContinue={handleContinue}
    >
      <section className="checklist-section">
        <h2>Ceilings</h2>
        <SelectInput label="Ceiling height" name="ceilingHeight" value={current.ceilingHeight} onChange={update} options={CEILING_HEIGHTS} />
        <MultiSelectInput label="Ceiling types" name="ceilingTypes" values={current.ceilingTypes || []} onChange={update} options={CEILING_TYPES} />
        <NotesField label="Ceiling notes" name="ceilingNotes" value={current.ceilingNotes} onChange={update} rows={3} placeholder="Popcorn, beams, volume in which rooms…" />
      </section>
      <section className="checklist-section">
        <h2>Walls</h2>
        <MultiSelectInput label="Wall types and finishes" name="wallTypes" values={current.wallTypes || []} onChange={update} options={WALL_TYPES} size={8} />
        <NotesField label="Wall notes" name="wallNotes" value={current.wallNotes} onChange={update} rows={3} placeholder="Accent walls, texture, or work needed." />
      </section>
      <section className="checklist-section">
        <h2>Flooring</h2>
        <MultiSelectInput label="Flooring types" name="flooring" values={current.flooring || []} onChange={update} options={FLOORING_TYPES} />
        <NotesField label="Flooring notes" name="flooringNotes" value={current.flooringNotes} onChange={update} rows={3} placeholder="Where each material appears, or condition." />
      </section>
      <section className="checklist-section">
        <h2>Architectural Features</h2>
        <MultiSelectInput
          label="Features present"
          name="architectural"
          values={current.architectural || []}
          onChange={update}
          options={ARCHITECTURAL_FEATURES}
          size={8}
        />
        <NotesField
          label="Architectural notes"
          name="architecturalNotes"
          value={current.architecturalNotes}
          onChange={update}
          rows={3}
          placeholder="Built-ins, light, or door styles worth highlighting."
        />
      </section>
      <section className="checklist-section">
        <h2>Kitchen Details</h2>
        <SelectInput label="Kitchen size" name="kitchenSize" value={current.kitchenSize} onChange={update} options={KITCHEN_SIZE} />
        <SelectInput
          label="Primary kitchen layout"
          name="kitchenPrimaryLayout"
          value={current.kitchenPrimaryLayout}
          onChange={update}
          options={KITCHEN_LAYOUTS}
        />
        <CheckboxGroup
          label="Kitchen layout features"
          name="kitchenLayout"
          values={current.kitchenLayout || []}
          onChange={update}
          layout="grid"
          options={KITCHEN_LAYOUTS}
        />
        <MultiSelectInput
          label="Kitchen details"
          name="kitchenDetails"
          values={current.kitchenDetails || []}
          onChange={update}
          options={KITCHEN_DETAILS}
          size={7}
        />
        <MultiSelectInput label="Countertops" name="countertops" values={current.countertops || []} onChange={update} options={COUNTERTOP_TYPES} />
        <MultiSelectInput label="Cabinets" name="cabinets" values={current.cabinets || []} onChange={update} options={CABINET_STYLES} />
        <NotesField label="Kitchen notes" name="kitchenNotes" value={current.kitchenNotes} onChange={update} rows={3} placeholder="Condition, appliances staying, or layout quirks." />
      </section>
      <section className="checklist-section">
        <h2>Bathroom Details</h2>
        <MultiSelectInput
          label="Bathroom features"
          name="bathroomDetails"
          values={current.bathroomDetails || []}
          onChange={update}
          options={BATHROOM_DETAILS}
          size={7}
        />
        <NotesField label="Bathroom notes" name="bathNotes" value={current.bathNotes} onChange={update} rows={3} placeholder="Primary bath quality, guest bath updates, or count." />
      </section>
      <section className="checklist-section">
        <h2>Bonus Rooms</h2>
        <MultiSelectInput label="Bonus rooms present" name="bonusRooms" values={current.bonusRooms || []} onChange={update} options={BONUS_ROOMS} size={7} />
        <NotesField label="Bonus-room notes" name="bonusNotes" value={current.bonusNotes} onChange={update} rows={3} placeholder="Office, ADU, or unfinished space." />
        <NotesField label="Interior summary notes" name="notes" value={current.notes} onChange={update} placeholder="Overall interior impression for this buyer." />
      </section>
    </AgentResponseLayout>
  );
}
